<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-2 px-2">
      <span class="text-caption"> Showing {{ paginationRangeText }} </span>
      <v-btn
        x-small
        text
        color="primary"
        @click="currentPage = 1"
        v-if="currentPage > 1"
      >
        Back to first page
      </v-btn>
    </div>

    <v-list-item
      v-for="(item, index) in paginatedItems"
      :key="index"
      class="feed-item px-0"
    >
      <v-list-item-content class="py-2">
        <v-row no-gutters align="center">
          <v-col cols="12" :sm="item.image || item.enclosure?.url ? 8 : 12">
            <div class="d-flex align-center mb-1">
              <v-avatar size="24" class="mr-2">
                <img
                  :src="
                    item.source?.feedImage || faviconImg(item.source?.domain)
                  "
                  @error="setAltImg"
                  class="source-logo"
                />
              </v-avatar>
              <div class="caption grey--text text--darken-2">
                {{ item.source?.title }} · {{ item.source?.categoryTitle }}
              </div>
            </div>
            <div
              class="subtitle-1 font-weight-bold cursor-pointer"
              @click="openLink(item.link)"
            >
              {{ item.title }}
            </div>
            <div class="caption primary--text">
              {{ formatDate(item.pubDate) }}
            </div>
            <div
              class="body-2 grey--text text--darken-1 mt-1"
              v-if="item.contentSnippet"
            >
              {{ truncateText(item.contentSnippet, 100) }}
            </div>
          </v-col>
          <v-col v-if="item.image || item.enclosure?.url" cols="12" sm="4">
            <v-img
              :src="item.image || item.enclosure?.url"
              max-height="100"
              contain
              class="rounded"
              @error="e => onImageError(e, item)"
            ></v-img>
          </v-col>
        </v-row>
        <div class="d-flex mt-2 flex-wrap">
          <v-btn
            small
            text
            color="primary"
            class="mr-2 mb-2"
            @click="$emit('bookmark', item)"
          >
            <v-icon small left>mdi-plus</v-icon>Add to Bookmarks
          </v-btn>
          <v-btn
            small
            text
            color="primary"
            class="mr-2 mb-2"
            @click="openLink(item.link)"
          >
            <v-icon small left>mdi-open-in-new</v-icon>Read
          </v-btn>
        </div>
      </v-list-item-content>
    </v-list-item>

    <v-divider class="my-4"></v-divider>

    <div class="pa-4">
      <paginate
        v-if="items.length > perPage"
        :page-count="pageCount"
        :click-handler="selectPage"
        :prev-text="'Prev'"
        :next-text="'Next'"
        :container-class="'pagination'"
        :page-class="'page-item'"
      />
      <div class="d-flex align-center justify-space-between mt-2">
        <span class="text-caption">{{ paginationRangeText }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Paginate from 'vuejs-paginate'

export default {
  name: 'RssFeedList',
  components: {
    Paginate
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    perPage: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      currentPage: 1
    }
  },
  computed: {
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.items.slice(start, end)
    },

    pageCount() {
      return Math.ceil(this.items.length / this.perPage)
    },

    paginationRangeText() {
      if (this.items.length === 0) return '0-0 of 0'
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = Math.min(this.currentPage * this.perPage, this.items.length)
      return `${start}-${end} of ${this.items.length}`
    }
  },
  methods: {
    openLink(url) {
      window.open(url, '_blank')
    },

    faviconImg(domain) {
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`
    },

    setAltImg($event) {
      $event.target.src = '/img/placeholder.jpg'
    },

    onImageError(e, item) {
      if (item) {
        this.$set(item, 'image', null)
        if (item.enclosure) {
          this.$set(item.enclosure, 'url', null)
        }
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
      const diffMinutes = Math.floor(diffTime / (1000 * 60))

      if (diffMinutes < 60) {
        return `${diffMinutes} minutes ago`
      } else if (diffHours < 24) {
        return `${diffHours} hours ago`
      } else if (diffDays === 0) {
        return 'Today'
      } else if (diffDays === 1) {
        return 'Yesterday'
      } else if (diffDays < 7) {
        return `${diffDays} days ago`
      } else {
        return date.toLocaleDateString()
      }
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    selectPage(page) {
      this.currentPage = page
    }
  }
}
</script>

<style scoped>
.feed-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding: 16px 0;
}

.feed-item:last-child {
  border-bottom: none;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s;
}

.cursor-pointer:hover {
  color: var(--v-primary-base);
}

.source-logo {
  object-fit: cover;
}
</style>
